// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { getUserCertificates, deleteCertificate } from '../firebase/services';
import StatusBadge from '../components/StatusBadge';

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (user) {
      getUserCertificates(user.uid)
        .then(setCerts)
        .finally(() => setLoading(false));
    }
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('dashboard.deleteConfirm'))) return;
    await deleteCertificate(id);
    setCerts(cs => cs.filter(c => c.id !== id));
  };

  const filtered = certs.filter(c =>
    (c.sample?.productName || c.product?.chemName || '').toLowerCase().includes(search.toLowerCase()) ||
    c.certId?.toLowerCase().includes(search.toLowerCase())
  );

  const thisMonth = certs.filter(c => {
    if (!c.createdAt?.seconds) return false;
    const d = new Date(c.createdAt.seconds * 1000);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const pendingCount  = certs.filter(c => c.status === 'pending').length;
  const approvedCount = certs.filter(c => c.status === 'approved').length;

  const formatDate = (ts) => {
    if (!ts?.seconds) return '—';
    return new Date(ts.seconds * 1000).toLocaleDateString();
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>{t('dashboard.title')}</h1>
          <p style={s.sub}>{t('dashboard.welcome')}, {user?.displayName || user?.email} 👋</p>
        </div>
        <Link to="/generate" style={s.generateBtn}>{t('dashboard.generate')}</Link>
      </div>

      {/* Stats */}
      <div style={s.statsGrid}>
        <div style={s.statCard}>
          <div style={s.statNum}>{certs.length}</div>
          <div style={s.statLabel}>{t('dashboard.total')}</div>
          <div style={s.statSub}>+{thisMonth} {t('dashboard.thisMonth')}</div>
        </div>
        <div style={{ ...s.statCard, borderColor: 'rgba(245,158,11,0.2)' }}>
          <div style={{ ...s.statNum, color: '#FBBF24' }}>{pendingCount}</div>
          <div style={s.statLabel}>{t('coa.pending.statusPending')}</div>
        </div>
        <div style={{ ...s.statCard, borderColor: 'rgba(52,211,153,0.2)' }}>
          <div style={{ ...s.statNum, color: '#34D399' }}>{approvedCount}</div>
          <div style={s.statLabel}>{t('coa.pending.statusApproved')}</div>
        </div>
        <div style={{ ...s.statCard, borderColor: 'rgba(74,158,255,0.2)' }}>
          <div style={{ ...s.statNum, color: '#4A9EFF' }}>{thisMonth}</div>
          <div style={s.statLabel}>{t('dashboard.thisMonth')}</div>
        </div>
      </div>

      {/* Table */}
      <div style={s.tableCard}>
        <div style={s.tableHeader}>
          <h2 style={s.tableTitle}>{t('dashboard.recent')}</h2>
          <input
            style={s.search}
            placeholder={t('common.search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div style={s.empty}>{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div style={s.emptyBox}>
            <div style={s.emptyIcon}>📄</div>
            <p style={s.emptyText}>{t('dashboard.noData')}</p>
            <Link to="/generate" style={s.generateBtn}>{t('dashboard.generate')}</Link>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>ID</th>
                  <th style={s.th}>{t('dashboard.product')}</th>
                  <th style={s.th}>{t('dashboard.batch')}</th>
                  <th style={s.th}>{t('dashboard.date')}</th>
                  <th style={s.th}>{t('dashboard.status')}</th>
                  <th style={s.th}>{t('dashboard.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(cert => (
                  <tr key={cert.id} style={s.tr}>
                    <td style={s.td}><span style={s.certId}>{cert.certId || cert.id.slice(0, 12)}</span></td>
                    <td style={s.td}>{cert.sample?.productName || cert.product?.chemName || '—'}</td>
                    <td style={s.td}>{cert.sample?.lot || cert.product?.batch || '—'}</td>
                    <td style={s.td}>{formatDate(cert.createdAt)}</td>
                    <td style={s.td}>
                      <StatusBadge status={cert.status || 'pending'} t={t} size="small" />
                    </td>
                    <td style={s.td}>
                      <div style={s.actions}>
                        <Link to={`/certificates/${cert.id}`} style={s.viewBtn}>{t('common.view')}</Link>
                        <button style={s.deleteBtn} onClick={() => handleDelete(cert.id)}>{t('common.delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#070F2B', padding: '84px 24px 48px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Inter', sans-serif", color: '#fff' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 },
  title: { fontSize: 30, fontWeight: 800, color: '#fff', margin: 0 },
  sub: { fontSize: 15, color: 'rgba(255,255,255,0.55)', marginTop: 4 },
  generateBtn: { background: 'linear-gradient(135deg, #1E5EFF, #0A3DD4)', color: '#fff', textDecoration: 'none', padding: '12px 22px', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'inline-block' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 16, marginBottom: 28 },
  statCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24 },
  statNum: { fontSize: 36, fontWeight: 900, color: '#fff', lineHeight: 1 },
  statLabel: { fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 6 },
  statSub: { fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 4 },
  tableCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, overflow: 'hidden' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexWrap: 'wrap', gap: 12 },
  tableTitle: { fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 },
  search: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '9px 14px', color: '#fff', fontSize: 14, outline: 'none', width: 220 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '14px 20px', fontSize: 14, color: 'rgba(255,255,255,0.75)' },
  certId: { fontFamily: 'monospace', fontSize: 12, color: '#4A9EFF' },
  actions: { display: 'flex', gap: 8 },
  viewBtn: { color: '#4A9EFF', textDecoration: 'none', fontSize: 13, fontWeight: 600, padding: '5px 12px', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 8 },
  deleteBtn: { background: 'none', border: '1px solid rgba(239,68,68,0.3)', color: '#F87171', fontSize: 13, cursor: 'pointer', padding: '5px 12px', borderRadius: 8 },
  empty: { padding: '48px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 15 },
  emptyBox: { padding: '60px 24px', textAlign: 'center' },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyText: { color: 'rgba(255,255,255,0.5)', fontSize: 15, marginBottom: 20 },
};
